import { io, type Socket } from "socket.io-client";
import { useEffect, useSyncExternalStore } from "react";
import type { ClientToServerEvents, ServerToClientEvents } from "@atrium/shared";

export type AppSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

let socket: AppSocket | null = null;
let status: "connecting" | "connected" | "disconnected" = "disconnected";
const listeners = new Set<() => void>();
const setStatus = (s: typeof status) => {
  status = s;
  listeners.forEach((l) => l());
};

/** Connect after authentication; the httpOnly session cookie authenticates the handshake. */
export function connectSocket(): AppSocket {
  if (socket) return socket;
  // tryAllTransports: networks that block WebSockets fall back to long-polling instead of never connecting.
  socket = io({ path: "/socket.io", withCredentials: true, transports: ["websocket", "polling"], tryAllTransports: true });
  setStatus("connecting");
  socket.on("connect", () => setStatus("connected"));
  socket.on("disconnect", () => setStatus("disconnected"));
  socket.io.on("reconnect_attempt", () => setStatus("connecting"));
  return socket;
}

export function disconnectSocket() {
  socket?.disconnect();
  socket = null;
  setStatus("disconnected");
}

export function getSocket(): AppSocket | null {
  return socket;
}

/**
 * Check a socket that looks connected really is. After sleep, a long-hidden tab or a
 * network switch it can be dead for a heartbeat or two while still reporting connected;
 * if the server doesn't answer in time, drop it so it reconnects (and recovers what it missed).
 */
export function probeConnection(timeoutMs = 4000) {
  const s = socket;
  if (!s?.connected) return;
  s.timeout(timeoutMs).emit("sync:probe", (err) => {
    if (err && s === socket && s.connected) s.io.engine.close();
  });
}

export function useSocketStatus() {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => status,
  );
}

/** Subscribe to a server event for the lifetime of a component. */
export function useSocketEvent<E extends keyof ServerToClientEvents>(event: E, handler: ServerToClientEvents[E], deps: unknown[] = []) {
  useEffect(() => {
    const s = socket;
    if (!s) return;
    s.on(event, handler as never);
    return () => {
      s.off(event, handler as never);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, socket, ...deps]);
}
